import { PropsWithChildren, useEffect, useRef } from 'react';
import { Route, RouteProps, Switch, useLocation } from 'react-router-dom';
import { PayloadActionCreator } from '@reduxjs/toolkit/src/createAction';

import { useAppDispatch } from '~/redux/store/useAppDispatch';

interface ILocationTracker {
  setPath: PayloadActionCreator<string | undefined>;
  condition?: boolean;
}

function LocationTracker({ setPath, condition }: ILocationTracker): null {
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const path = `${pathname}${search || ''}`;

  useEffect(() => {
    dispatch(setPath(condition ? path : undefined));
  }, [dispatch, setPath, condition, path]);

  return null;
}

interface ISwitchWithLocationTracker extends ILocationTracker {
  location: RouteProps['location'];
}

export default function SwitchWithLocationTracker({
  location,
  children,
  condition = true,
  ...props
}: PropsWithChildren<ISwitchWithLocationTracker>): JSX.Element {
  const lastRelevantLocation = useRef(location);

  if (condition) {
    lastRelevantLocation.current = location;
  }

  return (
    //  when navigating out router use previous location to see the route animating out
    <Route location={condition ? location : lastRelevantLocation.current}>
      <LocationTracker
        {...props}
        condition={condition}
      />
      <Switch>{children}</Switch>
    </Route>
  );
}
